<template>
  <Teleport to="body">
    <Transition name="reward-pop" appear>
      <div v-if="visible && reward" class="reward-overlay" @click.self="$emit('dismiss')">
        <!-- Confetti particles -->
        <div class="confetti-container">
          <div
            v-for="n in 30"
            :key="n"
            class="confetti-piece"
            :style="confettiStyle(n)"
          ></div>
        </div>

        <div class="reward-card">
          <div class="glow-ring"></div>
          <div class="text-6xl mb-4">🎉</div>
          <h2 class="text-2xl font-black text-white mb-2">Glückwunsch!</h2>
          <p class="text-white/50 text-sm mb-1">{{ gameName }}</p>
          <div class="reward-badge">
            <span class="text-3xl font-black text-yellow-400">{{ rewardText }}</span>
          </div>
          <p class="text-white/70 text-sm mt-3 px-4">{{ reward?.message }}</p>
          <button @click="$emit('redeem', reward)" class="redeem-btn mt-6">
            Jetzt einlösen
          </button>
          <button @click="$emit('dismiss')" class="later-btn mt-2">
            Später
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch } from 'vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  reward: { type: Object, default: null },
  gameName: { type: String, default: 'Mini-Spiel' },
})

defineEmits(['redeem', 'dismiss'])

const rewardText = computed(() => {
  if (!props.reward) return ''
  if (props.reward.type === 'freeItem') {
    const name = props.reward.productName || props.reward.product?.name
    if (name) return `Gratis: ${name}`
    return 'Gratis-Artikel!'
  }
  if (props.reward.type === 'discount' && props.reward.value) {
    return `${props.reward.value}% Rabatt`
  }
  return props.reward.message || 'Belohnung'
})

const confettiColors = [
  '#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7',
  '#FB923C', '#38BDF8', '#F472B6', '#34D399',
]

function confettiStyle(n) {
  const seed = n * 7 + 3
  const left = ((seed * 17) % 100)
  const delay = ((seed * 13) % 30) / 10
  const duration = 2.5 + ((seed * 11) % 20) / 10
  const color = confettiColors[n % confettiColors.length]
  const size = 6 + (seed % 6)
  const rotation = (seed * 23) % 360

  return {
    left: `${left}%`,
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    backgroundColor: color,
    width: `${size}px`,
    height: `${size}px`,
    '--start-rotation': `${rotation}deg`,
    borderRadius: n % 3 === 0 ? '50%' : n % 3 === 1 ? '2px' : '0',
  }
}

watch(() => props.visible, (val) => {
  if (val && navigator.vibrate) {
    navigator.vibrate([80, 40, 80, 40, 120])
  }
})
</script>

<style scoped>
.reward-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(12px);
}

.confetti-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.confetti-piece {
  position: absolute;
  top: -20px;
  opacity: 0;
  animation: confettiFall linear forwards;
}

@keyframes confettiFall {
  0% {
    transform: translateY(-10vh) rotate(var(--start-rotation, 0deg));
    opacity: 1;
  }
  75% {
    opacity: 1;
  }
  100% {
    transform: translateY(105vh) rotate(calc(var(--start-rotation, 0deg) + 720deg));
    opacity: 0;
  }
}

.reward-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 2rem 2rem;
  border-radius: 1.5rem;
  background: linear-gradient(165deg, rgba(20, 20, 40, 0.95), rgba(10, 10, 25, 0.98));
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow:
    0 0 60px rgba(255, 215, 0, 0.15),
    0 25px 60px rgba(0, 0, 0, 0.5);
  max-width: 340px;
  width: 90%;
  z-index: 1;
  overflow: hidden;
}

.glow-ring {
  position: absolute;
  top: -60%;
  left: -30%;
  width: 160%;
  height: 120%;
  background: radial-gradient(ellipse at center, rgba(255, 215, 0, 0.08), transparent 60%);
  pointer-events: none;
}

.reward-badge {
  margin-top: 0.75rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1rem;
  background: rgba(255, 215, 0, 0.08);
  border: 1px solid rgba(255, 215, 0, 0.2);
}

.redeem-btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  font-weight: 800;
  font-size: 1rem;
  color: #0a0a14;
  border-radius: 0.875rem;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  box-shadow: 0 4px 20px rgba(255, 215, 0, 0.3);
  transition: all 0.2s ease;
}
.redeem-btn:hover {
  box-shadow: 0 6px 28px rgba(255, 215, 0, 0.45);
  transform: translateY(-1px);
}
.redeem-btn:active {
  transform: scale(0.97);
}

.later-btn {
  padding: 0.5rem 1rem;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.875rem;
  transition: color 0.2s;
}
.later-btn:hover {
  color: rgba(255, 255, 255, 0.7);
}

/* Transition */
.reward-pop-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.reward-pop-leave-active {
  transition: all 0.25s ease-in;
}
.reward-pop-enter-from {
  opacity: 0;
}
.reward-pop-enter-from .reward-card {
  transform: scale(0.6) translateY(40px);
  opacity: 0;
}
.reward-pop-leave-to {
  opacity: 0;
}
.reward-pop-leave-to .reward-card {
  transform: scale(0.85) translateY(20px);
  opacity: 0;
}
</style>
